import { InputAdornment } from '@material-ui/core'
import React, {
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { useHotkeys } from 'react-hotkeys-hook'
import useIsMountedRef from 'use-is-mounted-ref'
import { TelSysConfiguration } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { TelSysAdminDTO, TelSysFirstSyncStatusResponse } from '../../../NonReact/Types/Types'
import { DEFAULT_PASSWORD_VALUE, EMPTY_STRING } from '../../../NonReact/constants/Common'
import { IsStringEmpty, isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { useFormContext, UseFormMethods, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { getOAuthConfig } from '../../Common/API/ConfigAPI'
import { ALL_VALUE, getSimulationModes } from '../../Common/API/ItemsGetters'
import { getTelSysProfiles } from '../../Common/API/Licence/LicenceAPI'
import { EmailType, sendEmailForUser } from '../../Common/API/UserAPIUtilities'
import FormRow from '../../Common/Components/FormRow/FormRow'
import Icon, { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { OAuthButton } from '../../Common/Components/OAuthButton/OAuthButton'
import { OAuthConfig, OAuthConfigOptions } from '../../Common/Components/OAuthButton/Types/Types'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import { SelectOptions } from '../../Common/Components/Selects'
import { ComboBoxItem, SelectOption } from '../../Common/Components/Selects/Types'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import { CommonFormContext, FormContextType } from '../../Common/Contexts/CommonFormContext'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import PromptContext, { PromptDisplayType } from '../../Common/Contexts/PromptContext'
import { useConfig } from '../../Common/Hooks/useConfig'
import { Rules } from '../../Common/Rules/Rules'
import { ProcessedTelSysProfile, TelSysIntegrationMode } from '../../Common/Types/Types'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { displaySuccessMessage } from '../../Common/Utils/Messages'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import {
    BUSINESS_OPTION,
    BusinessOption,
    DEFAULT_API_CONCURRENT_CONNECTIONS,
    DEFAULT_OUTBOUND_TEL_NO_PREFIX,
    DEFAULT_TEL_SYS_PROFILE_ID,
    INTEGRATION_TYPE,
    IntegrationType,
    LICENCE_VALUE_HIDDEN,
    LICENCE_VALUE_NONE,
    LICENCED_FOR_LIST,
    LicencedForRptTypes,
    SyncEnvironments,
    TEL_SYS_PROFILE_TO_FORM_VALUE_MAP,
    TEL_SYS_TYPE_VALUE_MAP,
    TELSYS_COMMS_STATUSES,
    TELSYS_MODAL_CONFIG_FILENAME,
    TelSysTypes
} from '../Constants/Constants'
import {
    BUSINESS_OPTIONS_FIELD_NAME,
    API_CONCURRENT_CONNECTIONS_FIELD_NAME,
    API_ENABLED_FIELD_NAME,
    USER_INACTIVITY_THRESHOLD_DAYS_FIELD_NAME,
    CALL_RECORDING_AUTHORISED_FIELD_NAME,
    CALL_RECORDING_ENABLED_FIELD_NAME,
    COMMS_ENABLED_FIELD_NAME,
    COMMS_PARAMETER_TEXT1_FIELD_NAME,
    COMMS_PARAMETER_TEXT2_FIELD_NAME,
    DELETE_ACD_AGENTS_NAME,
    ESSENTIALS_LICENSE_FIELD_NAME,
    IN_SERVICE_FIELD_NAME,
    INTEGRATION_MODE_FIELD_NAME,
    INTEGRATION_TYPE_FIELD_NAME,
    IP_ADDRESS1_FIELD_NAME,
    IP_ADDRESS2_FIELD_NAME,
    IP_ADDRESS3_FIELD_NAME,
    IP_ADDRESS4_FIELD_NAME,
    LICENCED_FOR_FIELD_NAME,
    CP_MONITORED_CQ_ENABLED_FIELD_NAME,
    OUTBOUND_TEL_NO_PREFIX_FIELD_NAME,
    PASSWORD1_FIELD_NAME,
    PASSWORD2_FIELD_NAME,
    PASSWORD3_FIELD_NAME,
    PASSWORD4_FIELD_NAME,
    PROFESSIONAL_LICENSE_FIELD_NAME,
    SIMULATION_MODE_FIELD_NAME,
    SYNC_ENV_ON_SETUP,
    TCP_PORT1_FIELD_NAME,
    TCP_PORT2_FIELD_NAME,
    TCP_PORT4_FIELD_NAME,
    TEL_SYS_PROFILE_FIELD_NAME,
    TelSysTabsFields,
    TYPE_FIELD_NAME,
    USE_SSLTLS2_FIELD_NAME,
    USE_SSLTLS4_FIELD_NAME,
    USER_NAME1_FIELD_NAME,
    USER_NAME2_FIELD_NAME,
    USER_NAME3_FIELD_NAME,
    USER_NAME4_FIELD_NAME,
    XSP_SERVER_FIELD_NAME
} from '../Constants/FieldNames'
import { TelSysForm } from '../Types/Types'
import { getDefaultBusinessRealTimeOptions } from '../Functions/DefaultBusinessOptions'
import { isBitmaskMatching } from '../../Common/Utils/BitwiseOperations'
import { CapabilityBitFlags } from '../../CMEAddModify/Constants'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import { isCxiTelSysType } from '../../Common/Utils/CxiEntitlementRules'
import {
    CX_SCORE_FEATURE_FLAG,
    ENABLE_CXI_DATA,
    SITE_SURVEY_FEATURE_FLAG,
    TELSYS_TRANSCRIPTION_OPTION_FEATURE_FLAG
} from '../../Common/Constants/FeatureFlags'
import {
    parseBusinessIntegrationOptions,
    shouldOfferCxScoreBusinessOption
} from '../../Common/Utils/CxProductApplicability'
import {
    isCmeGatedBusinessOptionAllowed,
    isTranscribeOptionAllowed,
    isTranscribeOptionQualified,
    shouldListTranscribeOption,
    withoutDisallowedCmeGatedOptions,
    withoutInvalidTranscribeOption
} from '../Functions/BusinessOptionCapabilityRules'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { OutlinedPasswordTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedPasswordTextInput'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { getTelSysFirstTimeSync } from '../../../NonReact/common/API/EntityAPIClient'
import { ENABLE_REAL_TIME_QUEUES_MANUAL_MODE } from '../../../ReactRouting/Constants/FeatureFlags'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { downloadSiteSurveyURL, runSiteSurvey } from '../../Common/API/SiteSurveyClient'
import {
    convertDateStringToDate,
    convertDateToPrettyDateString,
    isMoreThanDaysAgo,
    SupportedLocale
} from '../../Common/Utils/TimeFormats'
import { SiteSurveyStatusResult, useSiteSurveyStatus } from '../../Common/Hooks/useSiteSurveyStatus'
import {
    isCxiApiTelSysSupported,
    isCxiApiTelSysType,
    isCxiCapableForTelSysType,
    shouldShowApiControls as computeShouldShowApiControls
} from '../Functions/CxiApiTelSysRules'
import {
    CallRecordingAuthorisationState,
    isCallRecordingReauthorisationNeeded,
    isFormAuthorisationWithoutCallRecording,
    isWebexAuthorisationChangeUnsaved
} from '../Functions/CallRecordingAuthorisation'

type Props = {
    isEditMode: boolean
    viewOnly?: boolean
    selectedTelSys?: string
    syncStatus?: Nullable<string>
    originalTelSys?: JSONResponse<TelSysAdminDTO>
    availableTelSysProfiles: Nullable<ProcessedTelSysProfile[]>
    setAvailableTelSysProfiles: (value: Nullable<ProcessedTelSysProfile[]>) => void
    cmeCapabilities: number
    areCmeCapabilitiesLoaded: boolean
}

export const CommunicationsTab: FC<Props> = ({
    isEditMode,
    viewOnly,
    selectedTelSys,
    syncStatus,
    originalTelSys,
    availableTelSysProfiles,
    setAvailableTelSysProfiles,
    cmeCapabilities,
    areCmeCapabilitiesLoaded
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const { displayError }: MessageContextType = useContext(MessageContext)
    const [userInfo]: UserInfo = useUserInfo()

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)

    const {
        watch,
        setValue,
        clearErrors,
        formState: { dirtyFields, errors }
    }: UseFormMethods<TelSysForm> = useFormContext()

    const formContext: Nullable<FormContextType> = useContext(CommonFormContext)

    const defaultProfiles: Nullable<Record<TelSysTypes, TelSysForm[TelSysTabsFields.COMMUNICATIONS]>> = useConfig(
        TELSYS_MODAL_CONFIG_FILENAME,
        'defaultProfiles'
    )

    const { isSiteSurveyLoading, isSiteSurveySuccess, isSiteSurveyFailed }: SiteSurveyStatusResult =
        useSiteSurveyStatus(originalTelSys?.SiteSurveySyncStatus)

    const enableManualRTQueues: Definable<boolean> = useFeatureFlag(ENABLE_REAL_TIME_QUEUES_MANUAL_MODE)

    const [availableSimulationModes, setAvailableSimulationModes]: ReactState<SelectOptions<string>> = useState<
        SelectOptions<string>
    >([
        {
            label: language.get(TelSysConfiguration.TelSysConfiguration_NoneSelected),
            value: ALL_VALUE.toString()
        }
    ])
    const [oauthConfig, setOAuthConfig]: ReactState<Definable<OAuthConfig>> = useState<Definable<OAuthConfig>>()
    // Call recording setting that the Webex authorisation made in this form was granted under. Null until the admin
    // authorises here, so the server's saved authorisation stands.
    const [authorisedCallRecordingEnabled, setAuthorisedCallRecordingEnabled]: ReactState<Nullable<boolean>> =
        useState<Nullable<boolean>>(null)
    const oauthConfigCallRecordingEnabled: MutableRefObject<boolean> = useRef<boolean>(false)
    const authorisingCallRecordingEnabled: MutableRefObject<boolean> = useRef<boolean>(false)
    // Type the default Client ID was last filled in for, so it is filled in once per type change and not again after
    // the admin clears it to enter their own.
    const clientIDDefaultedForType: MutableRefObject<Definable<TelSysTypes>> = useRef<Definable<TelSysTypes>>()
    const [isWebexClientEditEnabled, setIsWebexClientEditEnabled]: ReactState<boolean> = useState<boolean>(false)
    const [firstSyncStatus, setFirstSyncStatus]: ReactState<boolean> = useState<boolean>(false)
    const [isTenantIDDisabled, setIsTenantIDDisabled]: ReactState<boolean> = useState<boolean>(isEditMode)
    const [isCPMonitoredCQDisabled, setIsCPMonitoredCQDisabled]: ReactState<boolean> = useState<boolean>(
        !!originalTelSys?.CommsEnabled
    )
    const [isSiteSurveyRunning, setIsSiteSurveyRunning]: ReactState<boolean> = useState<boolean>(isSiteSurveyLoading)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const telSysTypeValue: TelSysTypes = watch(TYPE_FIELD_NAME)
    const licenceForValue: string = watch(LICENCED_FOR_FIELD_NAME)
    const username1Value: string = watch(USER_NAME1_FIELD_NAME)
    const username2Value: string = watch(USER_NAME2_FIELD_NAME)
    const username3Value: string = watch(USER_NAME3_FIELD_NAME)
    const inServiceValue: boolean = watch(IN_SERVICE_FIELD_NAME)
    const newIntegrationModeValue: TelSysIntegrationMode = watch(INTEGRATION_MODE_FIELD_NAME)
    const integrationTypeValue: IntegrationType = watch(INTEGRATION_TYPE_FIELD_NAME)

    const isCxScoreFeatureEnabled: boolean = useFeatureFlag(CX_SCORE_FEATURE_FLAG, false)
    // Off unless features.yaml turns it on: a missing key reads false, so the option stays hidden until APD-14066 lands.
    const isTranscribeOptionEnabled: boolean = useFeatureFlag(TELSYS_TRANSCRIPTION_OPTION_FEATURE_FLAG, false)
    const apiEnabledValue: boolean = watch(API_ENABLED_FIELD_NAME)
    const isTenantAuthenticated: boolean = isStringNotEmpty(watch(USER_NAME2_FIELD_NAME))
    const callRecordingEnabledValue: boolean = !!watch(CALL_RECORDING_ENABLED_FIELD_NAME)

    // CXI-capable telsys type (Teams / Webex / BroadWorks); the CME capability is checked per use.
    const isCxiEnabled: boolean = isCxiTelSysType(telSysTypeValue)
    const isCxiDataEnabled: boolean = useFeatureFlag(ENABLE_CXI_DATA, false)
    const isCxiCapable: boolean = isCxiCapableForTelSysType(telSysTypeValue, cmeCapabilities)
    const shouldShowCXI: boolean = isCxiCapable && isCxiEnabled
    const isSiteSurveyEnabled: boolean = useFeatureFlag(SITE_SURVEY_FEATURE_FLAG, false)

    const telSysTypeValueUnderscored: string = telSysTypeValue.replace(/\s/g, '_')

    const isTelSysUnknown: boolean = telSysTypeValue === TelSysTypes.UNKNOWN
    const isTelSysSiemens: boolean = telSysTypeValue === TelSysTypes.SIEMENS_HIPATH_3000
    const isTelSysPanasonic: boolean = telSysTypeValue === TelSysTypes.PANASONIC_TDA_NCP
    const isTelSysBroadworks: boolean = telSysTypeValue === TelSysTypes.BROADSOFT_BROADWORKS
    const isTelSysBroadsoftM6: boolean = telSysTypeValue === TelSysTypes.BROADSOFT_M6
    const isTelSysWebexCalling: boolean = telSysTypeValue === TelSysTypes.WEBEX_CALLING
    const isTelSysSimulator: boolean = telSysTypeValue === TelSysTypes.SIMULATOR
    const isTelSysTeams: boolean = telSysTypeValue === TelSysTypes.MS_TEAMS
    const isTelSysCxiApi: boolean = isCxiApiTelSysType(telSysTypeValue)
    const isAuthorisationChangeUnsaved: boolean = isWebexAuthorisationChangeUnsaved(
        telSysTypeValue,
        dirtyFields?.communications,
        callRecordingEnabledValue,
        isVarNotEmpty(originalTelSys) ? !!originalTelSys?.CallRecordingEnabled : undefined
    )
    const callRecordingAuthorisationFor = (callRecordingEnabled: boolean): CallRecordingAuthorisationState => ({
        telSysType: telSysTypeValue,
        callRecordingEnabled,
        savedCallRecordingAuthorised: !!originalTelSys?.CallRecordingAuthorised,
        isTenantAuthenticated,
        authorisedCallRecordingEnabled
    })
    const callRecordingAuthorisation: CallRecordingAuthorisationState =
        callRecordingAuthorisationFor(callRecordingEnabledValue)
    const getCallRecordingWarning = (): string => {
        // While the change is unsaved the authorise button is hidden, so the admin is told to save before authorising.
        if (isAuthorisationChangeUnsaved) {
            return TelSysConfiguration.TelSysConfiguration_Warning_CallRecordingSaveThenReauthorise
        }

        return isFormAuthorisationWithoutCallRecording(callRecordingAuthorisation)
            ? TelSysConfiguration.TelSysConfiguration_Warning_CallRecordingReauthorise
            : TelSysConfiguration.TelSysConfiguration_Warning_CallRecordingNotAuthorised
    }
    const isTelSysInAuditMode: boolean =
        isSiteSurveyEnabled &&
        isVarNotEmpty(originalTelSys) &&
        originalTelSys?.IntegrationMode === TelSysIntegrationMode.CX_AUDIT
    const userHasCMEWidePermissions: boolean = isVarNotEmpty(userInfo) ? PermissionsUtil.isCMEWideUser(userInfo) : false

    const isWebexCapable: boolean = isBitmaskMatching(cmeCapabilities, CapabilityBitFlags.CXA_WEBEX)
    const isBroadworksCapable: boolean = isBitmaskMatching(cmeCapabilities, CapabilityBitFlags.CXA_BROADWORKS)
    const isTeamsCapable: boolean =
        isBitmaskMatching(cmeCapabilities, CapabilityBitFlags.CXA_TEAMS) ||
        isBitmaskMatching(cmeCapabilities, CapabilityBitFlags.CXI_TEAMS)
    const isLegacyCapable: boolean = isBitmaskMatching(cmeCapabilities, CapabilityBitFlags.CXA_LEGACY)
    const isCxiApiCapable: boolean = isCxiApiTelSysSupported(cmeCapabilities)
    const shouldShowApiControls: boolean = computeShouldShowApiControls(telSysTypeValue, cmeCapabilities)

    const shouldShowSiteSurveyButton: boolean =
        isSiteSurveyEnabled &&
        isTelSysTeams &&
        isEditMode &&
        (newIntegrationModeValue === TelSysIntegrationMode.CX_AUDIT ||
            (isVarNotEmpty(originalTelSys?.LastSiteSurveyRunStartedAt) &&
                isStringNotEmpty(originalTelSys?.LastSiteSurveyRunStartedAt)) ||
            (isVarNotEmpty(originalTelSys?.SiteSurveySyncStatus) &&
                isStringNotEmpty(originalTelSys?.SiteSurveySyncStatus)))

    const isMoreThan1DaysSinceLastSiteSurvey: boolean = isMoreThanDaysAgo(
        convertDateStringToDate(
            originalTelSys?.LastSiteSurveyRunStartedAt ?? EMPTY_STRING,
            language.getLanguageID() as SupportedLocale
        ),
        1
    )

    const syncEnvironmentOptions: SelectOptions<SyncEnvironments> = [
        {
            label: language.get(
                TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Sync_Environment_On_Setup_Options_Authoriser_Select
            ),
            value: SyncEnvironments.AUTHORISER_SELECT
        },
        {
            label: language.get(
                TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Sync_Environment_On_Setup_Options_Tenant
            ),
            value: SyncEnvironments.TENANT
        },
        {
            label: language.get(
                TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Sync_Environment_On_Setup_Options_Security_Groups
            ),
            value: SyncEnvironments.SECURITY_GROUPS
        }
    ]

    const shouldShowBroadworksXSICTI: boolean = ((): boolean => {
        if (isTelSysBroadworks && isCxiEnabled && newIntegrationModeValue === TelSysIntegrationMode.CXI_ONLY)
            return false

        if (isTelSysBroadworks) return true

        return false
    })()

    const telSysProfileOptions: SelectOptions<string> = useMemo(
        (): SelectOptions<string> =>
            availableTelSysProfiles
                ?.filter(
                    (telSysProfile: ProcessedTelSysProfile): boolean =>
                        telSysProfile?.type === TEL_SYS_TYPE_VALUE_MAP[telSysTypeValue]
                )
                ?.map(
                    (telSysProfile: ProcessedTelSysProfile): SelectOption<string> => ({
                        label: telSysProfile.Name,
                        value: telSysProfile.ID
                    })
                ) ?? [],
        [availableTelSysProfiles, telSysTypeValue]
    )

    const telsysTypeOptions: SelectOptions<TelSysTypes> = useMemo(
        (): SelectOptions<TelSysTypes> =>
            Object.values(TelSysTypes)
                .filter((telSysType: TelSysTypes): boolean => {
                    if (telSysType === TelSysTypes.WEBEX_CALLING) {
                        return isWebexCapable
                    }
                    if (telSysType === TelSysTypes.BROADSOFT_BROADWORKS) {
                        return isBroadworksCapable
                    }
                    if (telSysType === TelSysTypes.MS_TEAMS) {
                        return isTeamsCapable
                    }
                    if (telSysType === TelSysTypes.CXI_API) {
                        return isCxiDataEnabled && isCxiApiCapable
                    }
                    if (telSysType === TelSysTypes.UNKNOWN || telSysType === TelSysTypes.SIMULATOR) {
                        return true
                    }

                    return isLegacyCapable
                })
                .map(
                    (type: TelSysTypes): SelectOption<TelSysTypes> => ({
                        label: language.get(`TelSysConfiguration_TelSysType_${type}_Label`),
                        value: type
                    })
                ),
        [isBroadworksCapable, isWebexCapable, isTeamsCapable, isLegacyCapable, isCxiDataEnabled, isCxiApiCapable]
    )

    const telSysIntegrationModes: SelectOption<TelSysIntegrationMode>[] =
        useMemo((): SelectOption<TelSysIntegrationMode>[] => {
            const shouldShowAuditMode: boolean =
                isSiteSurveyEnabled &&
                (IsVarEmpty(originalTelSys) || newIntegrationModeValue === TelSysIntegrationMode.CX_AUDIT)
            const isBusiness: boolean = integrationTypeValue === INTEGRATION_TYPE.BUSINESS
            const shouldShowCXIOnly: boolean =
                !isBitmaskMatching(cmeCapabilities, CapabilityBitFlags.CXA_TEAMS) &&
                isBitmaskMatching(cmeCapabilities, CapabilityBitFlags.CXI_TEAMS)

            if (telSysTypeValue === TelSysTypes.BROADSOFT_BROADWORKS || telSysTypeValue === TelSysTypes.WEBEX_CALLING) {
                if (!shouldShowCXI) return []
                return [
                    {
                        label: language.get(
                            TelSysConfiguration.TelSysConfiguration_Integration_Mode_Reporting_And_CXI_Label
                        ),
                        value: TelSysIntegrationMode.CX_ANALYTICS
                    },
                    {
                        label: language.get(TelSysConfiguration.TelSysConfiguration_Integration_Mode_CXI_Only_Label),
                        value: TelSysIntegrationMode.CXI_ONLY
                    }
                ]
            }

            if (telSysTypeValue === TelSysTypes.MS_TEAMS) {
                if (shouldShowCXIOnly && !isBusiness) {
                    return [
                        {
                            label: language.get(
                                TelSysConfiguration.TelSysConfiguration_Integration_Mode_CXI_Only_Label
                            ),
                            value: TelSysIntegrationMode.CXI_ONLY
                        }
                    ]
                }

                const realTimeLabelKey: string = ((): string => {
                    if (isBusiness)
                        return TelSysConfiguration.TelSysConfiguration_Integration_Mode_Business_Real_Time_Label

                    return shouldShowCXI
                        ? TelSysConfiguration.TelSysConfiguration_Integration_Mode_Real_Time_And_CXI_Label
                        : TelSysConfiguration.TelSysConfiguration_Integration_Mode_Real_Time_Label
                })()

                const historicLabelKey: string = ((): string => {
                    if (isBusiness)
                        return TelSysConfiguration.TelSysConfiguration_Integration_Mode_Business_Historic_Label

                    return shouldShowCXI
                        ? TelSysConfiguration.TelSysConfiguration_Integration_Mode_Historic_And_CXI_Label
                        : TelSysConfiguration.TelSysConfiguration_Integration_Mode_Historic_Label
                })()

                return [
                    {
                        label: language.get(realTimeLabelKey),
                        value: TelSysIntegrationMode.REAL_TIME
                    },
                    {
                        label: language.get(historicLabelKey),
                        value: TelSysIntegrationMode.HISTORIC
                    },
                    ...(shouldShowAuditMode && !isBusiness
                        ? [
                              {
                                  label: language.get(
                                      TelSysConfiguration.TelSysConfiguration_Integration_Mode_CX_Audit
                                  ),
                                  value: TelSysIntegrationMode.CX_AUDIT
                              }
                          ]
                        : []),
                    ...(shouldShowCXI && !isBusiness
                        ? [
                              {
                                  label: language.get(
                                      TelSysConfiguration.TelSysConfiguration_Integration_Mode_CXI_Only_Label
                                  ),
                                  value: TelSysIntegrationMode.CXI_ONLY
                              }
                          ]
                        : [])
                ]
            }
            return []
        }, [
            telSysTypeValue,
            cmeCapabilities,
            shouldShowCXI,
            newIntegrationModeValue,
            originalTelSys,
            integrationTypeValue
        ])

    const shouldShowIntegrationMode: boolean = isCxiEnabled && telSysIntegrationModes?.length > 0

    const shouldShowBusinessOptions: boolean =
        integrationTypeValue === INTEGRATION_TYPE.BUSINESS &&
        telSysTypeValue === TelSysTypes.MS_TEAMS &&
        newIntegrationModeValue !== TelSysIntegrationMode.HISTORIC

    const integrationTypeOptions: SelectOption<IntegrationType>[] = useMemo(
        (): SelectOption<IntegrationType>[] => [
            {
                label: language.get(
                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Integration_Type_Legacy_Teams_Label
                ),
                value: INTEGRATION_TYPE.LEGACY_TEAMS
            },
            {
                label: language.get(
                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Integration_Type_Business_Label
                ),
                value: INTEGRATION_TYPE.BUSINESS
            }
        ],
        [language]
    )

    const storedBusinessOptions: Nullable<BusinessOption[]> = watch(BUSINESS_OPTIONS_FIELD_NAME)
    const hasStoredCxiOption: boolean = storedBusinessOptions?.includes(BUSINESS_OPTION.CX_INTEGRATION) ?? false

    // CX Score / CX Record need the CME capability for the telsys type (APD-13052).
    const savedBusinessOptions: Definable<readonly BusinessOption[]> = parseBusinessIntegrationOptions(
        originalTelSys?.IntegrationModeOptions
    )
    const loadedCmeCapabilities: Definable<number> = areCmeCapabilitiesLoaded ? cmeCapabilities : undefined
    const isCxScoreOptionAllowed: boolean = isCmeGatedBusinessOptionAllowed(
        BUSINESS_OPTION.CX_SCORE,
        telSysTypeValue,
        loadedCmeCapabilities,
        savedBusinessOptions
    )
    const isCxRecordOptionAllowed: boolean = isCmeGatedBusinessOptionAllowed(
        BUSINESS_OPTION.CX_RECORD,
        telSysTypeValue,
        loadedCmeCapabilities,
        savedBusinessOptions
    )

    // CX Transcribe needs the CME capability for Teams and CX Score or CX Record selected (APD-14047).
    const isTranscribeAllowed: boolean = isTranscribeOptionAllowed(loadedCmeCapabilities, savedBusinessOptions)
    const isTranscribeQualified: boolean = isTranscribeOptionQualified(storedBusinessOptions)
    const shouldOfferTranscribe: boolean = shouldListTranscribeOption(
        isTranscribeOptionEnabled,
        isTranscribeAllowed,
        isTranscribeQualified,
        storedBusinessOptions
    )

    const businessOptionItems: ComboBoxItem<BusinessOption>[] = useMemo((): ComboBoxItem<BusinessOption>[] => {
        const items: ComboBoxItem<BusinessOption>[] = [
            {
                value: BUSINESS_OPTION.CX_ANALYTICS,
                label: language.get(TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_CX_Option_CX_Analytics_Label)
            }
        ]

        if (isCxRecordOptionAllowed) {
            items.push({
                value: BUSINESS_OPTION.CX_RECORD,
                label: language.get(TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_CX_Option_CX_Record_Label)
            })
        }

        if (shouldShowCXI || hasStoredCxiOption) {
            items.push({
                value: BUSINESS_OPTION.CX_INTEGRATION,
                label: language.get(
                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_CX_Option_CX_Integration_Label
                )
            })
        }

        if (
            isCxScoreOptionAllowed &&
            shouldOfferCxScoreBusinessOption(isCxScoreFeatureEnabled, originalTelSys?.IntegrationModeOptions)
        ) {
            items.push({
                value: BUSINESS_OPTION.CX_SCORE,
                label: language.get(TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_CX_Option_CX_Score_Label)
            })
        }

        if (shouldOfferTranscribe) {
            items.push({
                value: BUSINESS_OPTION.CX_TRANSCRIBE,
                label: language.get(
                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_CX_Option_CX_Transcribe_Label
                )
            })
        }

        return items
    }, [
        language,
        isCxScoreFeatureEnabled,
        originalTelSys,
        shouldShowCXI,
        hasStoredCxiOption,
        isCxScoreOptionAllowed,
        isCxRecordOptionAllowed,
        shouldOfferTranscribe
    ])

    const defaultRealTimeBusinessOptions: BusinessOption[] = shouldShowCXI
        ? [BUSINESS_OPTION.CX_ANALYTICS, BUSINESS_OPTION.CX_INTEGRATION]
        : [BUSINESS_OPTION.CX_ANALYTICS]

    const handleIntegrationTypeChange = (newType: IntegrationType): void => {
        if (newType === INTEGRATION_TYPE.BUSINESS) {
            const currentMode: TelSysIntegrationMode = newIntegrationModeValue

            switch (currentMode) {
                case TelSysIntegrationMode.REAL_TIME:
                    setValue(BUSINESS_OPTIONS_FIELD_NAME, defaultRealTimeBusinessOptions)
                    break

                case TelSysIntegrationMode.HISTORIC:
                    setValue(BUSINESS_OPTIONS_FIELD_NAME, null)
                    break

                case TelSysIntegrationMode.CXI_ONLY:
                    setValue(INTEGRATION_MODE_FIELD_NAME, TelSysIntegrationMode.REAL_TIME)
                    setValue(
                        BUSINESS_OPTIONS_FIELD_NAME,
                        shouldShowCXI ? [BUSINESS_OPTION.CX_INTEGRATION] : defaultRealTimeBusinessOptions
                    )
                    break

                default:
                    setValue(INTEGRATION_MODE_FIELD_NAME, TelSysIntegrationMode.REAL_TIME)
                    setValue(BUSINESS_OPTIONS_FIELD_NAME, defaultRealTimeBusinessOptions)
                    break
            }
        }
    }

    const handleIntegrationModeChange = (newMode: TelSysIntegrationMode): void => {
        if (newMode === TelSysIntegrationMode.HISTORIC && integrationTypeValue === INTEGRATION_TYPE.BUSINESS) {
            setValue(BUSINESS_OPTIONS_FIELD_NAME, null)
        }

        setValue(CP_MONITORED_CQ_ENABLED_FIELD_NAME, newMode === TelSysIntegrationMode.CX_ANALYTICS)
    }

    const integrationModeGuard = (newMode: TelSysIntegrationMode): Promise<Definable<boolean>> => {
        if (!isEditMode || integrationTypeValue === INTEGRATION_TYPE.BUSINESS) return Promise.resolve(true)

        if (newMode === TelSysIntegrationMode.CXI_ONLY) {
            return displayPromptPromise(
                LanguageUtils.formatLanguageEntry(
                    language.get(
                        TelSysConfiguration.TelSysConfiguration_Field_Integration_Mode_Prompt_Message_CXI_Only_Label
                    )
                ),
                language.get(
                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Integration_Mode_Promt_Confirm_Label
                ),
                language.get(TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Integration_Mode_Promt_Cancel_Label)
            )
        }

        const isNewValueHistoric: boolean = newMode === TelSysIntegrationMode.HISTORIC
        return displayPromptPromise(
            LanguageUtils.formatLanguageEntry(
                language.get(
                    isNewValueHistoric
                        ? TelSysConfiguration.TelSysConfiguration_Field_Integration_Mode_Prompt_Message_Historic_Label
                        : TelSysConfiguration.TelSysConfiguration_Field_Integration_Mode_Prompt_Message_RealTime_Label
                )
            ),
            language.get(TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Integration_Mode_Promt_Confirm_Label),
            language.get(TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Integration_Mode_Promt_Cancel_Label)
        )
    }

    const licencedForOptions: SelectOptions<string> = LICENCED_FOR_LIST.map(
        (licence: LicencedForRptTypes): SelectOption<string> => ({
            label: language.get(`TelSysConfiguration_Field_LicencedFor_${licence.replace(/\s/g, '_')}_Label`),
            value: licence
        })
    )

    const processTelSysProfiles = useCallback(
        (telSysProfileLocal?: Nullable<ProcessedTelSysProfile>, overrideExisting?: boolean): void => {
            Object.entries(telSysProfileLocal?.LicenseValues ?? {})?.forEach(([key, value]: [string, string]): void => {
                if (
                    isStringNotEmpty(key) &&
                    isStringNotEmpty(value) &&
                    isStringNotEmpty(TEL_SYS_PROFILE_TO_FORM_VALUE_MAP[key]) &&
                    value !== LICENCE_VALUE_NONE &&
                    value !== LICENCE_VALUE_HIDDEN
                ) {
                    const currentValue: string = watch(TEL_SYS_PROFILE_TO_FORM_VALUE_MAP[key])

                    if (IsStringEmpty(currentValue) || overrideExisting) {
                        setValue(TEL_SYS_PROFILE_TO_FORM_VALUE_MAP[key], value)
                    }
                }
            })
        },
        [
            watch(TEL_SYS_PROFILE_TO_FORM_VALUE_MAP[COMMS_ENABLED_FIELD_NAME]),
            watch(TEL_SYS_PROFILE_TO_FORM_VALUE_MAP[ESSENTIALS_LICENSE_FIELD_NAME]),
            watch(TEL_SYS_PROFILE_TO_FORM_VALUE_MAP[PROFESSIONAL_LICENSE_FIELD_NAME])
        ]
    )

    const telSysChange = useCallback(
        (telSysTypeValueLocal: TelSysTypes): void => {
            setValue(TelSysTabsFields.COMMUNICATIONS, {
                type: telSysTypeValueLocal,
                apiEnabled: telSysTypeValueLocal === TelSysTypes.CXI_API,
                apiConcurrentConnections: DEFAULT_API_CONCURRENT_CONNECTIONS,
                licencedFor: EMPTY_STRING,
                ipAddress1: EMPTY_STRING,
                xspServer: EMPTY_STRING,
                tcpPort1: EMPTY_STRING,
                userName1: EMPTY_STRING,
                password1: EMPTY_STRING,
                ipAddress2: EMPTY_STRING,
                tcpPort2: EMPTY_STRING,
                useSSLTLS2: false,
                userName2: EMPTY_STRING,
                password2: EMPTY_STRING,
                oauthSessionID: EMPTY_STRING,
                ipAddress3: EMPTY_STRING,
                userName3: EMPTY_STRING,
                password3: EMPTY_STRING,
                simulateOmnichannelActivity: false,
                simulationMode: EMPTY_STRING,
                ipAddress4: EMPTY_STRING,
                tcpPort4: EMPTY_STRING,
                useSSLTLS4: false,
                userName4: EMPTY_STRING,
                password4: EMPTY_STRING,
                commsParameterText1: EMPTY_STRING,
                commsParameterText2: EMPTY_STRING,
                ...defaultProfiles?.[telSysTypeValueLocal]
            } as TelSysForm[TelSysTabsFields.COMMUNICATIONS])

            clearErrors()

            if (
                isStringNotEmpty(telSysTypeValueLocal) &&
                (telSysTypeValueLocal === TelSysTypes.BROADSOFT_BROADWORKS ||
                    telSysTypeValueLocal === TelSysTypes.WEBEX_CALLING ||
                    telSysTypeValueLocal === TelSysTypes.MS_TEAMS ||
                    telSysTypeValueLocal === TelSysTypes.UNKNOWN)
            ) {
                setValue(OUTBOUND_TEL_NO_PREFIX_FIELD_NAME, EMPTY_STRING)
            } else {
                setValue(OUTBOUND_TEL_NO_PREFIX_FIELD_NAME, DEFAULT_OUTBOUND_TEL_NO_PREFIX)
            }

            if (telSysTypeValueLocal === TelSysTypes.MS_TEAMS) {
                setValue(CP_MONITORED_CQ_ENABLED_FIELD_NAME, true)
            } else {
                setValue(CP_MONITORED_CQ_ENABLED_FIELD_NAME, false)
            }

            // Apply the default telSys profile first.
            processTelSysProfiles(
                availableTelSysProfiles?.find(
                    (tsProfile: ProcessedTelSysProfile): boolean => tsProfile.ID === DEFAULT_TEL_SYS_PROFILE_ID
                ) ?? null
            )
        },
        [defaultProfiles]
    )

    const runSiteSurveyReport = (): void => {
        runSiteSurvey(originalTelSys?.TelSysID as number)
            .then((): void => {
                if (isMounted.current) {
                    setIsSiteSurveyRunning(true)
                    displaySuccessMessage(
                        language.get(TelSysConfiguration.TelSysConfiguration_SiteSurvey_ReportRunSuccess)
                    )
                }
            })
            .catch(displayError)
    }

    const telSysProfileChange = useCallback(
        (telSysProfileValueLocal: string): void => {
            const telSysProfile: Nullable<ProcessedTelSysProfile> =
                availableTelSysProfiles?.find((tsProfile: ProcessedTelSysProfile): boolean =>
                    tsProfile.ID === DEFAULT_TEL_SYS_PROFILE_ID ? false : tsProfile.ID === telSysProfileValueLocal
                ) ?? null

            telSysChange(telSysTypeValue)

            if (isVarNotEmpty(telSysProfile) && telSysProfile?.ID !== DEFAULT_TEL_SYS_PROFILE_ID) {
                setValue(OUTBOUND_TEL_NO_PREFIX_FIELD_NAME, EMPTY_STRING)
            }

            processTelSysProfiles(telSysProfile, true)
        },
        [telSysTypeValue, availableTelSysProfiles, processTelSysProfiles]
    )

    const sendAuthEmail = (username: string): void => {
        sendEmailForUser(username, EmailType.AUTH_EMAIL)
            .then(
                (): Voidable<boolean> =>
                    displaySuccessMessage(language.get(TelSysConfiguration.TelSysConfiguration_ResendAuthEmail_Success))
            )
            .catch(displayError)
    }

    useHotkeys('ctrl+alt+c', (): void => setIsWebexClientEditEnabled((value: boolean): boolean => !value), {
        enableOnFormTags: true
    })

    useHotkeys(
        'ctrl+alt+t',
        (): void => {
            if (originalTelSys?.CommsEnabled === true) return

            setIsTenantIDDisabled((value: boolean): boolean => !value)
        },
        {
            enableOnFormTags: true
        }
    )

    useHotkeys('ctrl+alt+m', (): void => setIsCPMonitoredCQDisabled((current: boolean): boolean => !current), {
        enableOnFormTags: true
    })

    useEffect((): void => {
        if (!isEditMode) {
            getTelSysProfiles()
                .then((telSysProfiles: Nullable<ProcessedTelSysProfile[]>): void => {
                    if (
                        isVarNotEmpty(telSysProfiles) &&
                        (telSysProfiles.length > 1 ||
                            (telSysProfiles.length === 1 && telSysProfiles[0].ID !== DEFAULT_TEL_SYS_PROFILE_ID))
                    ) {
                        setAvailableTelSysProfiles(telSysProfiles)
                    } else {
                        setAvailableTelSysProfiles(null)
                    }
                })
                .catch(displayError)
        }

        getSimulationModes()
            .then((simulationModes: JSONResponse<SelectOptions<string>>): void =>
                setAvailableSimulationModes([
                    {
                        label: language.get(TelSysConfiguration.TelSysConfiguration_NoneSelected),
                        value: ALL_VALUE.toString()
                    },
                    ...(simulationModes ?? [])
                ])
            )
            .catch(displayError)
    }, [])

    useEffect((): void => {
        if (isVarNotEmpty(telSysProfileOptions) && telSysProfileOptions.length > 0) {
            const firstTelSysOptionValue: string = telSysProfileOptions[0].value

            setValue(TEL_SYS_PROFILE_FIELD_NAME, firstTelSysOptionValue)
            telSysProfileChange(firstTelSysOptionValue)
        } else {
            setValue(TEL_SYS_PROFILE_FIELD_NAME, EMPTY_STRING)
        }
    }, [JSON.stringify(telSysProfileOptions)])

    // Webex Calling only asks for access to recordings when call recording is on, so the config is fetched again
    // whenever the setting changes. A response that arrives after a newer request has started is ignored.
    useEffect((): Definable<() => void> => {
        if (
            IsVarEmpty(telSysTypeValue) ||
            (telSysTypeValue !== TelSysTypes.WEBEX_CALLING && telSysTypeValue !== TelSysTypes.MS_TEAMS)
        ) {
            clientIDDefaultedForType.current = telSysTypeValue

            return undefined
        }

        let isLatestRequest: boolean = true

        const options: Definable<OAuthConfigOptions> =
            telSysTypeValue === TelSysTypes.WEBEX_CALLING
                ? { callRecordingEnabled: callRecordingEnabledValue }
                : undefined

        getOAuthConfig(telSysTypeValue, options)
            .then((config: JSONResponse<OAuthConfig>): void => {
                if (!isLatestRequest) return

                oauthConfigCallRecordingEnabled.current = callRecordingEnabledValue
                setOAuthConfig(config ?? undefined)

                if (clientIDDefaultedForType.current === telSysTypeValue) return

                clientIDDefaultedForType.current = telSysTypeValue

                if (IsStringEmpty(watch(USER_NAME1_FIELD_NAME)) && telSysTypeValue === TelSysTypes.WEBEX_CALLING) {
                    setValue(USER_NAME1_FIELD_NAME, config?.ClientID)
                }
            })
            .catch(displayError)

        return (): void => {
            isLatestRequest = false
        }
    }, [telSysTypeValue, callRecordingEnabledValue])

    useEffect((): void => {
        if (isVarNotEmpty(selectedTelSys) && isTelSysTeams) {
            getTelSysFirstTimeSync(selectedTelSys)
                .then((res: JSONResponse<TelSysFirstSyncStatusResponse>): void => {
                    if (isMounted) {
                        setFirstSyncStatus(res?.FirstSyncCompleted ?? false)
                    }
                })
                .catch(displayError)
        }
    }, [])

    // Remove a stored CX Integration option once the CME is known to lack the CXI capability, so a
    // telsys saved before the capability was removed does not keep the option. Waits for the CME
    // capabilities to load (they start at 0) and runs before the seeding effect so an emptied list
    // is refilled with a valid default.
    useEffect((): void => {
        if (!areCmeCapabilitiesLoaded || isCxiCapable) return

        const currentOptions: Nullable<BusinessOption[]> = watch(BUSINESS_OPTIONS_FIELD_NAME)

        if (!currentOptions?.includes(BUSINESS_OPTION.CX_INTEGRATION)) return

        const remainingOptions: BusinessOption[] = currentOptions.filter(
            (option: BusinessOption): boolean => option !== BUSINESS_OPTION.CX_INTEGRATION
        )

        setValue(BUSINESS_OPTIONS_FIELD_NAME, remainingOptions.length > 0 ? remainingOptions : null)
    }, [isCxiCapable, areCmeCapabilitiesLoaded])

    // Remove CX Score / CX Record once the CME is known not to allow them for the telsys type (APD-13052), e.g. after
    // the CME or telsys type changes on a new telsys. An option the saved telsys already has is allowed, so it is kept.
    useEffect((): void => {
        const currentOptions: Nullable<BusinessOption[]> = watch(BUSINESS_OPTIONS_FIELD_NAME)

        if (!currentOptions) return

        const remainingOptions: BusinessOption[] = withoutDisallowedCmeGatedOptions(
            currentOptions,
            isCxScoreOptionAllowed,
            isCxRecordOptionAllowed
        )

        if (remainingOptions.length === currentOptions.length) return

        setValue(BUSINESS_OPTIONS_FIELD_NAME, remainingOptions.length > 0 ? remainingOptions : null)
    }, [isCxScoreOptionAllowed, isCxRecordOptionAllowed])

    // Remove CX Transcribe when it loses its qualifying CX Score / CX Record, or the CME stops allowing it. The feature
    // flag only hides the option; it doesn't remove a saved one, so turning the flag off doesn't wipe TelSys settings.
    useEffect((): void => {
        const currentOptions: Nullable<BusinessOption[]> = watch(BUSINESS_OPTIONS_FIELD_NAME)

        if (!currentOptions) return

        const remainingOptions: BusinessOption[] = withoutInvalidTranscribeOption(currentOptions, isTranscribeAllowed)

        if (remainingOptions.length === currentOptions.length) return

        setValue(BUSINESS_OPTIONS_FIELD_NAME, remainingOptions.length > 0 ? remainingOptions : null)
    }, [isTranscribeAllowed, isTranscribeQualified])

    // Keep a valid CX option set whenever the telsys sits in Business Real-Time, which the server
    // requires. Two paths otherwise leave the options empty: a new telsys accepting the Business
    // default (APD-13306), and switching Historic -> Real-Time (which clears them). The empty-only
    // guard means an existing user selection is never overwritten.
    useEffect((): void => {
        const defaultBusinessOptions: Nullable<BusinessOption[]> = getDefaultBusinessRealTimeOptions({
            telSysType: telSysTypeValue,
            integrationType: integrationTypeValue,
            integrationMode: newIntegrationModeValue,
            currentBusinessOptions: watch(BUSINESS_OPTIONS_FIELD_NAME),
            isCxiAvailable: shouldShowCXI
        })

        if (isVarNotEmpty(defaultBusinessOptions)) {
            setValue(BUSINESS_OPTIONS_FIELD_NAME, defaultBusinessOptions)
        }
    }, [telSysTypeValue, integrationTypeValue, newIntegrationModeValue, shouldShowCXI, areCmeCapabilitiesLoaded])

    return (
        <div className='communication-tab tab-area'>
            <FormRow>
                <ValidationErrors name={TYPE_FIELD_NAME}>
                    <OutlinedSelect
                        name={TYPE_FIELD_NAME}
                        label={language.get(TelSysConfiguration.TelSysConfiguration_Field_TelSysType_Label)}
                        options={telsysTypeOptions}
                        disabled={viewOnly || isEditMode}
                        handleChange={telSysChange}
                        rules={{
                            validate: {
                                isNotUnknown: (value: string): ValidateResult => {
                                    if (value === TelSysTypes.UNKNOWN) {
                                        return language.get(
                                            TelSysConfiguration.TelSysConfiguration_Field_TelSysType_Error
                                        )
                                    }

                                    return undefined
                                }
                            }
                        }}
                    />
                </ValidationErrors>
            </FormRow>
            {!isTelSysUnknown ? (
                <>
                    {isEditMode || IsVarEmpty(telSysProfileOptions) || telSysProfileOptions.length === 0 ? (
                        <></>
                    ) : (
                        <FormRow>
                            <OutlinedSelect
                                name={TEL_SYS_PROFILE_FIELD_NAME}
                                label={language.get(TelSysConfiguration.TelSysConfiguration_Field_TelSysProfile_Label)}
                                options={telSysProfileOptions}
                                disabled={viewOnly}
                                handleChange={telSysProfileChange}
                            />
                        </FormRow>
                    )}
                    {shouldShowApiControls ? (
                        <>
                            <FormRow>
                                <RouterCheckbox
                                    name={API_ENABLED_FIELD_NAME}
                                    labelLeft={language.get(
                                        TelSysConfiguration.TelSysConfiguration_Field_APIEnabled_Label
                                    )}
                                    disabled={viewOnly}
                                />
                            </FormRow>
                            {apiEnabledValue ? (
                                <>
                                    <FormRow>
                                        <ValidationErrors name={API_CONCURRENT_CONNECTIONS_FIELD_NAME}>
                                            <OutlinedTextInput
                                                inputName={API_CONCURRENT_CONNECTIONS_FIELD_NAME}
                                                labelLeft={language.get(
                                                    TelSysConfiguration.TelSysConfiguration_Field_APIConcurrentConnections_Label
                                                )}
                                                disabled={viewOnly}
                                                rules={{
                                                    required: language.get(
                                                        TelSysConfiguration.TelSysConfiguration_Error_Required
                                                    ),
                                                    validate: {
                                                        isNumber: Rules.isNumber(language),
                                                        isPositiveNumber: Rules.isPositiveNumber(language)
                                                    }
                                                }}
                                            />
                                        </ValidationErrors>
                                    </FormRow>
                                    <FormRow>
                                        <ValidationErrors name={USER_INACTIVITY_THRESHOLD_DAYS_FIELD_NAME}>
                                            <OutlinedTextInput
                                                inputName={USER_INACTIVITY_THRESHOLD_DAYS_FIELD_NAME}
                                                labelLeft={language.get(
                                                    TelSysConfiguration.TelSysConfiguration_Field_UserInactivityThresholdDays_Label
                                                )}
                                                disabled={viewOnly}
                                                rules={{
                                                    required: language.get(
                                                        TelSysConfiguration.TelSysConfiguration_Error_Required
                                                    ),
                                                    validate: {
                                                        isInteger: Rules.isInteger(language),
                                                        isInRange: Rules.isInRange(language, undefined, 1, 3650)
                                                    }
                                                }}
                                            />
                                        </ValidationErrors>
                                    </FormRow>
                                </>
                            ) : (
                                <></>
                            )}
                        </>
                    ) : (
                        <></>
                    )}
                    {isTelSysSimulator ? (
                        <FormRow>
                            <OutlinedSelect
                                name={SIMULATION_MODE_FIELD_NAME}
                                label={language.get(TelSysConfiguration.TelSysConfiguration_Field_SimulationMode_Label)}
                                options={availableSimulationModes}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysSiemens || isTelSysPanasonic ? (
                        <>
                            <FormRow>
                                <OutlinedSelect
                                    name={LICENCED_FOR_FIELD_NAME}
                                    label={language.get(
                                        TelSysConfiguration.TelSysConfiguration_Field_LicencedFor_Label
                                    )}
                                    options={licencedForOptions}
                                    disabled={viewOnly}
                                />
                            </FormRow>
                            {isEditMode &&
                            !!dirtyFields?.communications?.licencedFor &&
                            (licenceForValue === LicencedForRptTypes.LITE ||
                                licenceForValue === LicencedForRptTypes.ONE_THOUSAND) ? (
                                <FormRow className='important-field'>
                                    <RouterCheckbox
                                        name={DELETE_ACD_AGENTS_NAME}
                                        labelLeft={language.get(
                                            TelSysConfiguration.TelSysConfiguration_Field_DeleteACDAgents_Label
                                        )}
                                        disabled={viewOnly}
                                    />
                                </FormRow>
                            ) : (
                                <></>
                            )}
                        </>
                    ) : (
                        <></>
                    )}
                    {shouldShowIntegrationMode && telSysTypeValue === TelSysTypes.MS_TEAMS ? (
                        <FormRow className='integration-type'>
                            <OutlinedSelect<IntegrationType>
                                name={INTEGRATION_TYPE_FIELD_NAME}
                                handleChange={handleIntegrationTypeChange}
                                label={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Integration_Type_Label
                                )}
                                options={integrationTypeOptions}
                                disabled={
                                    (isEditMode && originalTelSys?.CommsEnabled) ||
                                    !userHasCMEWidePermissions ||
                                    viewOnly
                                }
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {shouldShowIntegrationMode ? (
                        <FormRow className='integration-mode'>
                            <OutlinedSelect<TelSysIntegrationMode>
                                name={INTEGRATION_MODE_FIELD_NAME}
                                label={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Integration_Mode_Label
                                )}
                                options={telSysIntegrationModes}
                                handleChange={handleIntegrationModeChange}
                                guardFunction={integrationModeGuard}
                                disabled={
                                    ((isEditMode && originalTelSys?.CommsEnabled) ||
                                        !userHasCMEWidePermissions ||
                                        viewOnly) &&
                                    originalTelSys?.IntegrationMode !== TelSysIntegrationMode.CX_AUDIT
                                }
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {shouldShowBusinessOptions ? (
                        <FormRow className='business-options'>
                            <ComboBox<BusinessOption>
                                variant='outlined'
                                name={BUSINESS_OPTIONS_FIELD_NAME}
                                label={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Business_Options_Label
                                )}
                                defaultOption={businessOptionItems[0]}
                                options={businessOptionItems.slice(1)}
                                multiple
                                requireOneSelected
                                disabled={
                                    (isEditMode && originalTelSys?.CommsEnabled) ||
                                    !userHasCMEWidePermissions ||
                                    viewOnly
                                }
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {telSysTypeValue === TelSysTypes.MS_TEAMS && !isEditMode ? (
                        <FormRow className='sync-env-on-setup'>
                            <OutlinedSelect
                                name={SYNC_ENV_ON_SETUP}
                                label={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Sync_Environment_On_Setup_Label
                                )}
                                options={syncEnvironmentOptions}
                                defaultValue={SyncEnvironments.TENANT}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {shouldShowIntegrationMode &&
                    telSysTypeValue === TelSysTypes.MS_TEAMS &&
                    enableManualRTQueues &&
                    newIntegrationModeValue === TelSysIntegrationMode.REAL_TIME ? (
                        <FormRow className='cp-monitored-real-time-queues'>
                            <RouterCheckbox
                                name={CP_MONITORED_CQ_ENABLED_FIELD_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_MS_Teams_Bot_For_Real_Time_Queue_Label
                                )}
                                disabled={
                                    (isEditMode && isCPMonitoredCQDisabled) || !userHasCMEWidePermissions || viewOnly
                                }
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysSiemens || isTelSysPanasonic || shouldShowBroadworksXSICTI || isTelSysBroadsoftM6 ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={IP_ADDRESS1_FIELD_NAME}
                                labelLeft={language.get(
                                    `TelSysConfiguration_Field_${telSysTypeValueUnderscored}_IPAddress1_Label`
                                )}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysSiemens || isTelSysPanasonic ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={TCP_PORT1_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_TCPPort1_Label)}
                                disabled={viewOnly}
                                rules={{
                                    validate: {
                                        isNumber: Rules.isNumber(language)
                                    }
                                }}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysWebexCalling ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={USER_NAME3_FIELD_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_Webex_Calling_Username3_Label
                                )}
                                rules={{ validate: { isValidEmailAddress: Rules.isEmailValid(language) } }}
                                disabled={viewOnly}
                                adornmentEnd={
                                    !viewOnly &&
                                    isEditMode &&
                                    isStringNotEmpty(username3Value) &&
                                    !errors?.communications?.userName3 &&
                                    !dirtyFields?.communications?.userName3 ? (
                                        <InputAdornment position='end' className='input-toggle'>
                                            <IconButton
                                                onClick={(): void => sendAuthEmail(username3Value)}
                                                iconName={IconName.SEND}
                                                type={IconButtonType.BUTTON}
                                                buttonType='button'
                                            />
                                        </InputAdornment>
                                    ) : (
                                        <></>
                                    )
                                }
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {(isTelSysWebexCalling && isWebexClientEditEnabled) || isTelSysTeams ? (
                        <FormRow>
                            <ValidationErrors name={USER_NAME1_FIELD_NAME}>
                                <OutlinedTextInput
                                    inputName={USER_NAME1_FIELD_NAME}
                                    labelLeft={language.get(
                                        `TelSysConfiguration_Field_${telSysTypeValueUnderscored}_Username1_Label`
                                    )}
                                    disabled={viewOnly}
                                    rules={
                                        isTelSysTeams
                                            ? {
                                                  validate: {
                                                      isValidEmailAddress: Rules.isEmailValid(language)
                                                  }
                                              }
                                            : undefined
                                    }
                                    adornmentEnd={
                                        !viewOnly &&
                                        isEditMode &&
                                        isTelSysTeams &&
                                        isStringNotEmpty(username1Value) &&
                                        !errors?.communications?.userName1 &&
                                        !dirtyFields?.communications?.userName1 ? (
                                            <InputAdornment position='end' className='input-toggle'>
                                                <IconButton
                                                    onClick={(): void => sendAuthEmail(username1Value)}
                                                    iconName={IconName.SEND}
                                                    type={IconButtonType.BUTTON}
                                                    buttonType='button'
                                                />
                                            </InputAdornment>
                                        ) : (
                                            <></>
                                        )
                                    }
                                />
                            </ValidationErrors>
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysWebexCalling && isWebexClientEditEnabled ? (
                        <FormRow>
                            <OutlinedPasswordTextInput
                                inputName={PASSWORD1_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_Password1_Label)}
                                disabled={viewOnly}
                                placeholder={DEFAULT_PASSWORD_VALUE}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadsoftM6 || shouldShowBroadworksXSICTI ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={IP_ADDRESS2_FIELD_NAME}
                                labelLeft={language.get(
                                    `TelSysConfiguration_Field_${telSysTypeValueUnderscored}_IPAddress2_Label`
                                )}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadsoftM6 || shouldShowBroadworksXSICTI ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={TCP_PORT2_FIELD_NAME}
                                labelLeft={language.get(
                                    `TelSysConfiguration_Field_${telSysTypeValueUnderscored}_TCPPort2_Label`
                                )}
                                disabled={viewOnly}
                                rules={{
                                    validate: {
                                        isNumber: Rules.isNumber(language)
                                    }
                                }}
                                adornmentEnd={
                                    isTelSysBroadworks ? (
                                        <RouterCheckbox
                                            name={USE_SSLTLS2_FIELD_NAME}
                                            disabled={viewOnly}
                                            popupHover={
                                                <>
                                                    {language.get(
                                                        TelSysConfiguration.TelSysConfiguration_Field_UseSSLTLS2_Label
                                                    )}
                                                </>
                                            }
                                        />
                                    ) : undefined
                                }
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {shouldShowBroadworksXSICTI ||
                    ((isTelSysWebexCalling || isTelSysTeams) && isEditMode && isStringNotEmpty(username2Value)) ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={USER_NAME2_FIELD_NAME}
                                labelLeft={language.get(
                                    `TelSysConfiguration_Field_${telSysTypeValueUnderscored}_Username2_Label`
                                )}
                                disabled={viewOnly || isTelSysWebexCalling || isTelSysTeams}
                                onBlur={(value?: string): void => {
                                    if (isTelSysBroadworks && IsStringEmpty(watch(USER_NAME4_FIELD_NAME))) {
                                        setValue(USER_NAME4_FIELD_NAME, value)
                                    }
                                }}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {shouldShowBroadworksXSICTI ? (
                        <FormRow>
                            <OutlinedPasswordTextInput
                                inputName={PASSWORD2_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_Password2_Label)}
                                disabled={viewOnly}
                                onBlur={(value?: string): void => {
                                    if (isTelSysBroadworks && IsStringEmpty(watch(PASSWORD4_FIELD_NAME))) {
                                        setValue(PASSWORD4_FIELD_NAME, value)
                                    }
                                }}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadsoftM6 || isTelSysBroadworks ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={IP_ADDRESS3_FIELD_NAME}
                                labelLeft={language.get(
                                    `TelSysConfiguration_Field_${telSysTypeValueUnderscored}_IPAddress3_Label`
                                )}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadsoftM6 ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={USER_NAME3_FIELD_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_BroadSoft_M6_AuthUserID3_Label
                                )}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadsoftM6 ? (
                        <FormRow>
                            <OutlinedPasswordTextInput
                                inputName={PASSWORD3_FIELD_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_BroadSoft_M6_AuthPassword3_Label
                                )}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadworks ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={IP_ADDRESS4_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_IPAddress4_Label)}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadworks ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={TCP_PORT4_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_TCPPort4_Label)}
                                disabled={viewOnly}
                                rules={{
                                    validate: {
                                        isNumber: Rules.isNumber(language)
                                    }
                                }}
                                adornmentEnd={
                                    <RouterCheckbox
                                        name={USE_SSLTLS4_FIELD_NAME}
                                        disabled={viewOnly}
                                        popupHover={
                                            <>
                                                {language.get(
                                                    TelSysConfiguration.TelSysConfiguration_Field_UseSSLTLS4_Label
                                                )}
                                            </>
                                        }
                                    />
                                }
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadworks ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={USER_NAME4_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_Username4_Label)}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadworks ? (
                        <FormRow>
                            <OutlinedPasswordTextInput
                                inputName={PASSWORD4_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_Password4_Label)}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysBroadworks ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={XSP_SERVER_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_XSPServer_Label)}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {shouldShowBroadworksXSICTI || isTelSysTeams || (isEditMode && isTelSysWebexCalling) ? (
                        <FormRow>
                            <ValidationErrors name={COMMS_PARAMETER_TEXT1_FIELD_NAME}>
                                <OutlinedTextInput
                                    inputName={COMMS_PARAMETER_TEXT1_FIELD_NAME}
                                    labelLeft={language.get(
                                        `TelSysConfiguration_Field_${telSysTypeValueUnderscored}_CommsParameter1_Label`
                                    )}
                                    disabled={
                                        viewOnly ||
                                        (isTelSysTeams && isTenantIDDisabled) ||
                                        (isEditMode && isTelSysWebexCalling)
                                    }
                                    rules={
                                        isTelSysTeams
                                            ? {
                                                  required: language.get(
                                                      TelSysConfiguration.TelSysConfiguration_Error_Required
                                                  )
                                              }
                                            : undefined
                                    }
                                />
                            </ValidationErrors>
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {shouldShowBroadworksXSICTI ? (
                        <FormRow>
                            <OutlinedTextInput
                                inputName={COMMS_PARAMETER_TEXT2_FIELD_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_CommsParameter2_Label
                                )}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysWebexCalling ? (
                        <FormRow>
                            <RouterCheckbox
                                name={CALL_RECORDING_ENABLED_FIELD_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_CallRecordingEnabled_Label
                                )}
                                title={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_CallRecordingEnabled_Tooltip
                                )}
                                disabled={viewOnly}
                                rules={{
                                    validate: {
                                        matchesFormAuthorisation: (value: boolean): ValidateResult =>
                                            !isAuthorisationChangeUnsaved &&
                                            isFormAuthorisationWithoutCallRecording(
                                                callRecordingAuthorisationFor(!!value)
                                            )
                                                ? language.get(
                                                      TelSysConfiguration.TelSysConfiguration_Warning_CallRecordingReauthorise
                                                  )
                                                : undefined
                                    }
                                }}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isCallRecordingReauthorisationNeeded(callRecordingAuthorisation) ? (
                        <>
                            <div className='warning-info'>{language.get(getCallRecordingWarning())}</div>
                            <br />
                        </>
                    ) : (
                        <></>
                    )}
                    {(isTelSysWebexCalling || isTelSysTeams) && !isAuthorisationChangeUnsaved && !viewOnly ? (
                        <OAuthButton
                            shrink
                            isRouterMode
                            config={oauthConfig}
                            overrideClientID={isTelSysTeams ? undefined : watch(USER_NAME1_FIELD_NAME, undefined)}
                            isAlreadyAuthenticated={isTenantAuthenticated}
                            integrationType={telSysTypeValue}
                            selectedTelSys={selectedTelSys}
                            lastError={syncStatus}
                            forceRelogin={telSysTypeValue === TelSysTypes.WEBEX_CALLING}
                            onAuthenticating={(): void => {
                                authorisingCallRecordingEnabled.current = oauthConfigCallRecordingEnabled.current
                                // Saved with the sign-in's session ID, so it is set as the sign-in starts. Toggling call
                                // recording hides this button and drops its success callback, and the setting saved
                                // must still be the scope this sign-in asked for.
                                setValue(CALL_RECORDING_AUTHORISED_FIELD_NAME, authorisingCallRecordingEnabled.current)
                            }}
                            onOAuthSuccess={(): void => {
                                setAuthorisedCallRecordingEnabled(authorisingCallRecordingEnabled.current)
                            }}
                            oAuthMessageOptions={{
                                authorisationButtonTitle: language.get(
                                    TelSysConfiguration.TelSysConfiguration_OAuth_AuthorisationButtonLabel
                                ),
                                authorisedMessage: language.get(
                                    TelSysConfiguration.TelSysConfiguration_OAuth_AuthorisedMessage
                                ),
                                preAuthorisationButtonTitle: language.get(
                                    TelSysConfiguration.TelSysConfiguration_OAuth_PreAuthorisationButtonLabel
                                ),
                                preAuthorisedMessage: language.get(
                                    TelSysConfiguration.TelSysConfiguration_OAuth_PreAuthorisedMessage
                                )
                            }}
                        />
                    ) : (
                        <></>
                    )}
                    {shouldShowSiteSurveyButton ? (
                        <>
                            <div className='form-row site-survey'>
                                <div className='label'>
                                    <span className='site-survey-label'>
                                        {language.get(TelSysConfiguration.TelSysConfiguration_SiteSurvey_Title)}
                                    </span>
                                    <span className='site-survey-date'>
                                        {language.getInterpolated(
                                            TelSysConfiguration.TelSysConfiguration_SiteSurvey_LastRun,
                                            IsStringEmpty(originalTelSys?.LastSiteSurveyRunStartedAt)
                                                ? language.get(TelSysConfiguration.TelSysConfiguration_SiteSurvey_Never)
                                                : convertDateToPrettyDateString(
                                                      convertDateStringToDate(
                                                          originalTelSys?.LastSiteSurveyRunStartedAt ?? EMPTY_STRING,
                                                          language.getLanguageID() as SupportedLocale
                                                      )
                                                  )
                                        )}
                                    </span>
                                </div>
                                <div className='survey-results'>
                                    <RoundedButton
                                        disabled={
                                            isSiteSurveyLoading ||
                                            !originalTelSys?.CommsEnabled ||
                                            originalTelSys?.CommsStatus?.includes(TELSYS_COMMS_STATUSES.DISABLED) ||
                                            originalTelSys?.CommsStatus?.includes(TELSYS_COMMS_STATUSES.INACTIVE) ||
                                            isSiteSurveyRunning ||
                                            !isTenantAuthenticated ||
                                            (isStringNotEmpty(
                                                originalTelSys?.LastSiteSurveyRunStartedAt ?? EMPTY_STRING
                                            ) &&
                                                !isMoreThan1DaysSinceLastSiteSurvey)
                                        }
                                        onClick={(): void => runSiteSurveyReport()}
                                        shrink
                                        isOutlined
                                    >
                                        {originalTelSys?.LastSiteSurveyRunStartedAt === EMPTY_STRING &&
                                        !isMoreThan1DaysSinceLastSiteSurvey &&
                                        !isSiteSurveyRunning ? (
                                            <>
                                                <Icon iconName={IconName.PLAY} />
                                                {language.get(TelSysConfiguration.TelSysConfiguration_SiteSurvey_Run)}
                                            </>
                                        ) : (
                                            <>
                                                <Icon iconName={IconName.REFRESH} />
                                                {language.get(
                                                    isSiteSurveyRunning
                                                        ? TelSysConfiguration.TelSysConfiguration_SiteSurvey_Running
                                                        : TelSysConfiguration.TelSysConfiguration_SiteSurvey_ReRun
                                                )}
                                            </>
                                        )}
                                    </RoundedButton>
                                    {isSiteSurveySuccess ? (
                                        <RoundedButton
                                            download
                                            shrink
                                            href={downloadSiteSurveyURL(
                                                originalTelSys?.TelSysID as number
                                            ).constructURL()}
                                        >
                                            <Icon iconName={IconName.DOWNLOAD} />
                                        </RoundedButton>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </div>
                            {isSiteSurveyFailed && (
                                <div className='vc-message'>
                                    {language.get(TelSysConfiguration.TelSysConfiguration_SiteSurvey_Error_Message)}
                                </div>
                            )}
                        </>
                    ) : (
                        <></>
                    )}
                    {isTelSysWebexCalling && isAuthorisationChangeUnsaved ? (
                        <>
                            <div className='warning-info'>
                                {language.get(TelSysConfiguration.TelSysConfiguration_Warning_SaveFirst)}
                            </div>
                            <br />
                        </>
                    ) : (
                        <></>
                    )}
                    {!isTelSysCxiApi ? (
                        <FormRow>
                            <RouterCheckbox
                                name={COMMS_ENABLED_FIELD_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_CommsEnabled_Label
                                )}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTelSysTeams ? (
                        <FormRow>
                            <RouterCheckbox
                                name={IN_SERVICE_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_InService_Label)}
                                disabled={
                                    viewOnly ||
                                    (!firstSyncStatus && !isTelSysInAuditMode) ||
                                    (!formContext?.dirtyFields[IN_SERVICE_FIELD_NAME] && inServiceValue)
                                }
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                </>
            ) : (
                <></>
            )}
        </div>
    )
}
